// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AutocompleteProductTile, {
    AutocompleteProductTileModel,
} from "@mcmc/Overrides/Widgets/Header/AutocompleteProductTile";
import mergeToNew from "@mcmc/XDL/Utilities/xMergeToNew";

import { Roboto, RobotoCondensed } from "@mcmc/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

interface Props {
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
    productTiles: AutocompleteProductTileModel[];
    query: string;
    doSearch: (sectionId?: string) => void;
    isEvents: boolean;
    disableAddToCartActions?: boolean;
}

export interface AutocompleteProductsStyles {
    heading?: InjectableCss;
    headerText?: TypographyPresentationProps;
    headerLink?: LinkPresentationProps;
    productsWrapper?: InjectableCss;
    container?: InjectableCss;
    focusedContainer?: InjectableCss;
}

const productContainerStyle = css`
    display: flex;
    position: relative;
    flex: 0 1 50%;
    padding: 15px 8px;
    cursor: pointer;
    min-width: 0;
    &::before {
        content: "";
        display: block;
        border-top: 1px solid ${getColor("common.border")};
        position: absolute;
        top: -1px;
        left: 50%;
        transform: translateX(-50%);
        width: calc(100% - 16px);
    }

    ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(
            theme,
            [
                null,
                css`
                    flex: 1 1 100%;
                `,
            ],
            "max",
        )}
`;

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    heading: {
        css: css`
            flex: 1 1 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            padding: 10px 0;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    headerText: {
        ...RobotoCondensed,
        variant: "h5",
        weight: 700,
        css: css`
            text-align: left;
            margin: 0 10px 0 0;
            letter-spacing: 0;
        `,
    },
    headerLink: {
        typographyProps: {
            ...Roboto,
        },
    },
    productsWrapper: {
        css: css`
            flex: 1 1 100%;
            display: flex;
            flex-wrap: wrap;
            margin: 0 -8px -15px -8px;
        `,
    },
    container: {
        css: productContainerStyle,
    },
    focusedContainer: {
        css: css`
            ${productContainerStyle}
            > div {
                background-color: ${getColor("common.ui4")};
            }
        `,
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
        this.doSearch = this.doSearch.bind(this);
        this.doEventsSearch = this.doEventsSearch.bind(this);
    }

    doSearch() {
        this.props.doSearch();
    }

    doEventsSearch() {
        this.props.doSearch("events");
    }

    render() {
        const { productTiles, query, isEvents, goToUrl, disableAddToCartActions } = this.props;
        const styles = this.styles;
        return (
            <>
                {isEvents && (
                    <StyledWrapper {...styles.heading}>
                        <Typography {...styles.headerText}>
                            {translate("Training Results for ")} “{query}”
                        </Typography>
                        <Link onClick={this.doEventsSearch} {...styles.headerLink}>
                            {translate("View All Training Results")}
                        </Link>
                    </StyledWrapper>
                )}
                {!isEvents && (
                    <StyledWrapper {...styles.heading}>
                        <Typography {...styles.headerText}>
                            {translate("Product Results for ")} “{query}”
                        </Typography>
                        <Link onClick={this.doSearch} {...styles.headerLink}>
                            {translate("View All Product Results")}
                        </Link>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.productsWrapper}>
                    {productTiles.map((productTile, i) => (
                        <StyledWrapper
                            {...(this.props.focusedItem === productTile ? styles.focusedContainer : styles.container)}
                            key={productTile.product.id!}
                            onClick={() => {
                                this.props.goToUrl(productTile.product.canonicalUrl);
                            }}
                            className="autocomplete-products-container"
                        >
                            <AutocompleteProductTile
                                {...productTile}
                                isEvent={isEvents}
                                goToUrl={goToUrl}
                                disableAddToCart={disableAddToCartActions}
                            ></AutocompleteProductTile>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            </>
        );
    }
}

export default AutocompleteProducts;
